
import React, { useState, useMemo, useEffect } from 'react';
import './App.css';
import { AppContext, User, Post, Product, ProductOffer, CartItem, Order, Message, Notification, Analytics, Comment } from './context/AppContext';
import { adminUser, buyerUser, testSubscriber, userCredentials, mockPosts, mockStories, mockProducts, mockOrders, mockMessages, subscriptionTiers, mockAnalytics } from './data/mockData';
import Header from './components/Header/Header';
import BottomNav from './components/BottomNav/BottomNav';
import HomePage from './features/Home/HomePage';
import ShopPage from './features/Shop/ShopPage';
import GuestShopPage from './features/Shop/GuestShopPage';
import CartPage from './features/Cart/CartPage';
import MessagesPage from './features/Messages/MessagesPage';
import ProfilePage from './features/Profile/ProfilePage';
import ProductDetail from './features/ProductDetail/ProductDetail';
import AdminDashboard from './features/Admin/AdminDashboard';
import LandingPage from './features/Landing/LandingPage';
import SignInPage from './features/Auth/SignInPage';
import FeaturedSpotlightModal from './components/FeaturedSpotlightModal/FeaturedSpotlightModal';
import FirstLoginTutorial, { hasTutorialBeenSeen } from './components/FirstLoginTutorial/FirstLoginTutorial';
import SignOutPage from './features/Auth/SignOutPage';
import PendingPage from './features/Auth/PendingPage';
import { authAPI, postsAPI, mediaAPI, profileAPI, commentsAPI, offersAPI, notificationsAPI, productsAPI, setTokens, clearTokens, isAuthenticated as checkAuthTokens, getIsAdminFromToken, hasStoredToken, isTokenExpired, getAccessToken } from './services/api';
import { ToastContainer } from './components/Toast/Toast';
import AdminPreviewBar from './components/AdminPreviewBar/AdminPreviewBar';
import { captureUtmOnLoad } from './utils/utm';

// Pending user info stored in localStorage
interface PendingUserInfo {
  email: string;
  displayName: string;
  selectedTier: string;
  paymentLink?: string;
  createdAt: string;
  /** True when this entry represents an expired subscriber being routed
   *  through the renewal flow, not a brand-new signup. */
  renewal?: boolean;
  /** ISO timestamp of the prior period_end (when renewal=true). */
  periodEnd?: string | null;
}

const loadPendingUser = (): PendingUserInfo | null => {
  try {
    const saved = localStorage.getItem('onlyjalen_pending_user');
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error('Error loading pending user:', e);
  }
  return null;
};

const savePendingUser = (info: PendingUserInfo) => {
  localStorage.setItem('onlyjalen_pending_user', JSON.stringify(info));
};

const clearPendingUser = () => {
  localStorage.removeItem('onlyjalen_pending_user');
};

// Required fields for a valid User object
const REQUIRED_USER_FIELDS = ['id', 'username', 'email', 'displayName'];

// Validate user object has required fields
const isValidUser = (obj: unknown): obj is User => {
  if (!obj || typeof obj !== 'object') return false;
  const user = obj as Record<string, unknown>;
  return REQUIRED_USER_FIELDS.every(field =>
    field in user && user[field] !== null && user[field] !== undefined
  );
};

// Helper to load user from localStorage with schema validation
const loadSavedUser = (): { user: User | null; isAdmin: boolean } => {
  try {
    const saved = localStorage.getItem('onlyjalen_user');
    if (saved) {
      const parsed = JSON.parse(saved);

      // Schema validation - reject if missing required fields
      if (!isValidUser(parsed)) {
        console.warn('Invalid user data in localStorage, clearing...');
        localStorage.removeItem('onlyjalen_user');
        return { user: null, isAdmin: false };
      }

      // Check admin status from stored user data
      const isAdmin = parsed.isAdmin === true;

      // Scrub a known-bad URL that an earlier build baked in as a
      // hardcoded cover image fallback (404s, rendered as broken-image X).
      // Strip it so pre-existing sessions stop showing the giant X.
      const scrubbedCoverImage = parsed.coverImage === 'https://jalenward.com/images/covephoto.png'
        ? ''
        : parsed.coverImage;

      return {
        user: {
          ...parsed,
          coverImage: scrubbedCoverImage,
          joinDate: parsed.joinDate ? new Date(parsed.joinDate) : new Date(),
          subscriptionExpiry: parsed.subscriptionExpiry ? new Date(parsed.subscriptionExpiry) : null,
          isAdmin,
        },
        isAdmin,
      };
    }
  } catch (e) {
    console.error('Error loading saved user:', e);
    // Clear corrupted data
    try { localStorage.removeItem('onlyjalen_user'); } catch {}
  }
  return { user: null, isAdmin: false };
};

// Helper to save user to localStorage (strips sensitive fields)
const saveUser = (user: User): boolean => {
  try {
    // Don't persist isAdmin - it comes from JWT on load
    const { ...userToSave } = user;
    localStorage.setItem('onlyjalen_user', JSON.stringify(userToSave));
    return true;
  } catch (e) {
    console.error('Error saving user:', e);
    // Could be QuotaExceededError - user should know session won't persist
    return false;
  }
};

// Helper to clear saved user
const clearSavedUser = () => {
  try { localStorage.removeItem('onlyjalen_user'); } catch {}
};

// Helper to load posts from localStorage
const loadSavedPosts = (): Post[] => {
  try {
    const saved = localStorage.getItem('onlyjalen_posts');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Convert timestamp strings back to Date objects
      return parsed.map((p: Post) => ({
        ...p,
        timestamp: new Date(p.timestamp),
        scheduledFor: p.scheduledFor ? new Date(p.scheduledFor) : undefined,
        expiresAt: p.expiresAt ? new Date(p.expiresAt) : undefined,
      }));
    }
  } catch (e) {
    console.error('Error loading saved posts:', e);
  }
  return [];
};

// Helper to get initial posts (saved + mock)
const getInitialPosts = (): Post[] => {
  const savedPosts = loadSavedPosts();
  const mockData = [...mockStories, ...mockPosts];

  // Merge: saved posts first (user created), then mock posts that aren't duplicates
  const savedIds = new Set(savedPosts.map(p => p.id));
  const uniqueMockPosts = mockData.filter(p => !savedIds.has(p.id));

  return [...savedPosts, ...uniqueMockPosts];
};

// Helper to load messages from localStorage
const loadSavedMessages = (): Message[] => {
  try {
    const saved = localStorage.getItem('onlyjalen_messages');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map((m: Message) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      }));
    }
  } catch (e) {
    console.error('Error loading saved messages:', e);
  }
  return [];
};

// Helper to get initial messages (saved + mock)
const getInitialMessages = (): Message[] => {
  const savedMessages = loadSavedMessages();
  // Merge: saved messages first, then mock messages that aren't duplicates
  const savedIds = new Set(savedMessages.map(m => m.id));
  const uniqueMockMessages = mockMessages.filter(m => !savedIds.has(m.id));
  return [...savedMessages, ...uniqueMockMessages];
};

// Helper to load comments from localStorage
const loadSavedComments = (): Map<string, Comment[]> => {
  try {
    const saved = localStorage.getItem('onlyjalen_comments');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Convert array back to Map and restore Date objects
      const map = new Map<string, Comment[]>();
      for (const [postId, comments] of parsed) {
        map.set(postId, comments.map((c: Comment) => ({
          ...c,
          timestamp: new Date(c.timestamp),
        })));
      }
      return map;
    }
  } catch (e) {
    console.error('Error loading saved comments:', e);
  }
  return new Map();
};

// Helper to load product offers from localStorage and merge with mock products
const getInitialProducts = (): Product[] => {
  const baseProducts = [...mockProducts];
  try {
    const saved = localStorage.getItem('onlyjalen_product_offers');
    if (saved) {
      const savedOffers = JSON.parse(saved);
      // Merge saved offers into products
      return baseProducts.map(product => {
        const savedProduct = savedOffers.find((p: any) => p.id === product.id);
        if (savedProduct) {
          return {
            ...product,
            offers: savedProduct.offers?.map((o: ProductOffer) => ({
              ...o,
              timestamp: new Date(o.timestamp),
            })) || [],
            topOffer: savedProduct.topOffer,
          };
        }
        return product;
      });
    }
  } catch (e) {
    console.error('Error loading saved product offers:', e);
  }
  return baseProducts;
};

function App() {
  // Initialize auth state from persisted tokens/user
  const savedSession = loadSavedUser();
  const hasValidTokens = checkAuthTokens();
  // Try JWT first, fall back to stored user data
  const isAdminFromToken = getIsAdminFromToken();
  const isAdminUser = isAdminFromToken || savedSession.isAdmin;

  const [isAuthenticated, setIsAuthenticated] = useState(hasValidTokens && !!savedSession.user);
  const [showSignIn, setShowSignIn] = useState(window.location.pathname === '/signin' || window.location.pathname === '/login');
  // Initial tab in SignInPage. LandingPage's "Get Started" CTA sets
  // this to 'signup' so new visitors land directly on the Create
  // Account / Join as Subscriber screen instead of the login form.
  const [signInMode, setSignInMode] = useState<'signin' | 'signup'>('signin');
  // Only trigger signout flow if authenticated AND on signout URL
  const [showSignOut, setShowSignOut] = useState(
    (hasValidTokens && !!savedSession.user) &&
    (window.location.pathname === '/signout' || window.location.pathname === '/logout')
  );
  const [pendingUser, setPendingUser] = useState<PendingUserInfo | null>(loadPendingUser);
  const [isAdminView, setIsAdminView] = useState(isAdminUser);
  // First-login tutorial — only flagged true if the user is authenticated,
  // is a subscriber (not admin), and hasn't seen the tutorial yet on this
  // browser. localStorage flag set to '1' once dismissed so it never
  // reappears for the same browser/user.
  const [showTutorial, setShowTutorial] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return !hasTutorialBeenSeen();
  });
  const [activeTab, setActiveTab] = useState(() => {
    // Parse URL path on initial load
    const path = window.location.pathname.replace('/', '');
    const validTabs = ['home', 'shop', 'cart', 'messages', 'profile'];
    if (validTabs.includes(path)) return path;
    if (path === 'dashboard') return 'home'; // dashboard maps to home for admins
    return 'home';
  });

  // Guest-mode pathname. Lets cold (unauth) visitors navigate between
  // the landing page and the public /shop view without a full reload.
  // Synced with window.history pushState/popstate.
  const [guestPath, setGuestPath] = useState<string>(window.location.pathname);
  const navigateGuest = (path: string) => {
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
    setGuestPath(path);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };
  useEffect(() => {
    const onPop = () => setGuestPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // UTM attribution. First-touch capture happens SYNCHRONOUSLY before
  // any child component renders, so the Reddit-aware landing variant
  // doesn't flash the default subscription hero on first paint.
  // Safe to call sync: it's URL parse + localStorage write, no DOM.
  // useState initializer ensures it runs exactly once per mount.
  useState(() => { captureUtmOnLoad(); return null; });
  const [viewMode, setViewMode] = useState<'dark' | 'light'>(() => {
    if (typeof window === 'undefined') return 'dark';
    const saved = localStorage.getItem('oj_theme');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });
  // Sync theme to <html data-theme=...> + localStorage
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = viewMode;
    }
    try { localStorage.setItem('oj_theme', viewMode); } catch {}
  }, [viewMode]);

  // PayPal return: if URL has ?paid=true&subscriptionId=X, activate the sub
  // and surface a welcome toast. Works for both fresh signups and re-subs.
  // Also handles ?renew=1 deep-link from the renewal nudge email by
  // routing the user to the sign-in screen if not already there.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get('paid');
    const subId = params.get('subscriptionId');
    const paypalToken = params.get('token') || params.get('paypalToken') || undefined;
    const renew = params.get('renew');

    if (paid === 'true' && subId) {
      (async () => {
        try {
          const { subscriptionsAPI } = await import('./services/api');
          await subscriptionsAPI.activate({
            subscriptionId: subId,
            paypalOrderId: paypalToken,
            paypalSubscriptionId: paypalToken,
            type: 'subscription' as any,
          } as any);
          // Clean the URL so refresh doesn't reactivate
          const url = new URL(window.location.href);
          ['paid', 'subscriptionId', 'token', 'paypalToken'].forEach(k => url.searchParams.delete(k));
          window.history.replaceState({}, '', url.toString());
        } catch (e) {
          console.warn('Activation on return failed:', e);
        }
      })();
    }

    if (renew === '1') {
      // Pop the sign-in sheet so the user can authenticate; once they
      // do, the backend will return 402-expired and we route them into
      // the renewal-mode PendingPage automatically.
      setShowSignIn(true);
      const url = new URL(window.location.href);
      url.searchParams.delete('renew');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const [posts, setPosts] = useState<Post[]>(getInitialPosts);
  const [products, setProducts] = useState<Product[]>(getInitialProducts);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>(mockOrders);
  const [messages, setMessages] = useState<Message[]>(getInitialMessages);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [currentUser, setCurrentUser] = useState<User>(savedSession.user || buyerUser);
  const [analytics] = useState<Analytics>(mockAnalytics);
  const [comments, setComments] = useState<Map<string, Comment[]>>(loadSavedComments);

  // Featured-product spotlight — shown once per (subscriber, product).
  // Triggered after auth when a freshly-featured item exists that this
  // particular subscriber hasn't dismissed yet. Storage key carries the
  // userId so different fans on the same browser each see it once.
  const [spotlightProduct, setSpotlightProduct] = useState<Product | null>(null);
  const spotlightStorageKey = (userId: string, productId: string) => `oj_spotlight_seen:${userId}:${productId}`;

  // Trigger spotlight check on auth + product changes. Skip admins
  // (Jalen doesn't need to be spotlighted for his own listing).
  useEffect(() => {
    if (spotlightProduct) return;
    if (!isAuthenticated) return;
    if (isAdminView) return;
    if (!currentUser?.isSubscribed) return;
    if (!currentUser?.id) return;
    if (!products || products.length === 0) return;

    const candidates = products
      .filter(p => p.featured)
      .sort((a, b) => {
        const aT = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt as any).getTime();
        const bT = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt as any).getTime();
        return bT - aT;
      });

    for (const candidate of candidates) {
      let seen = false;
      try {
        seen = localStorage.getItem(spotlightStorageKey(currentUser.id, candidate.id)) === '1';
      } catch { /* private mode etc — fall through and show */ }
      if (!seen) {
        setSpotlightProduct(candidate);
        return;
      }
    }
  }, [isAuthenticated, isAdminView, currentUser?.id, currentUser?.isSubscribed, products, spotlightProduct]);

  const dismissSpotlight = (action: 'view' | 'later') => {
    if (!spotlightProduct || !currentUser?.id) {
      setSpotlightProduct(null);
      return;
    }
    try {
      localStorage.setItem(spotlightStorageKey(currentUser.id, spotlightProduct.id), '1');
    } catch { /* best-effort */ }
    if (action === 'view') {
      setActiveTab('shop');
    }
    setSpotlightProduct(null);
  };

  // Fetch posts on mount if authenticated (defined after fetchPostsFromAPI below)
  const shouldFetchOnMount = isAuthenticated;

  // Persist posts to localStorage when they change
  useEffect(() => {
    try {
      // Only save user-created posts (those with IDs starting with 'p' followed by timestamp)
      const userCreatedPosts = posts.filter(p => p.id.startsWith('p') && p.id.length > 5);
      localStorage.setItem('onlyjalen_posts', JSON.stringify(userCreatedPosts));
    } catch (e) {
      console.error('Error saving posts:', e);
    }
  }, [posts]);

  // Persist messages to localStorage when they change
  useEffect(() => {
    try {
      // Only save user-created messages (those with numeric IDs from Date.now())
      const userCreatedMessages = messages.filter(m => /^\d+$/.test(m.id));
      localStorage.setItem('onlyjalen_messages', JSON.stringify(userCreatedMessages));
    } catch (e) {
      console.error('Error saving messages:', e);
    }
  }, [messages]);

  // Persist comments to localStorage when they change
  useEffect(() => {
    try {
      // Convert Map to array of [postId, comments[]] for storage
      const commentsArray = Array.from(comments.entries());
      if (commentsArray.length > 0) {
        localStorage.setItem('onlyjalen_comments', JSON.stringify(commentsArray));
      }
    } catch (e) {
      console.error('Error saving comments:', e);
    }
  }, [comments]);

  // Persist product offers to localStorage when products change
  useEffect(() => {
    try {
      // Only save products that have offers
      const productsWithOffers = products
        .filter(p => p.offers && p.offers.length > 0)
        .map(p => ({ id: p.id, offers: p.offers, topOffer: p.topOffer }));
      if (productsWithOffers.length > 0) {
        localStorage.setItem('onlyjalen_product_offers', JSON.stringify(productsWithOffers));
      }
    } catch (e) {
      console.error('Error saving product offers:', e);
    }
  }, [products]);

  // Clean up expired offers periodically
  useEffect(() => {
    const cleanupExpiredOffers = () => {
      const now = new Date();
      setProducts(prev => prev.map(product => {
        if (!product.offers || product.offers.length === 0) return product;

        const updatedOffers = product.offers.map(offer => {
          if (offer.status === 'pending' && new Date(offer.expiresAt) < now) {
            return { ...offer, status: 'expired' as const };
          }
          return offer;
        });

        // Recalculate top offer from non-expired pending offers
        const activeOffers = updatedOffers.filter(o => o.status === 'pending');
        const topOffer = activeOffers.length > 0
          ? Math.max(...activeOffers.map(o => o.amount))
          : undefined;

        return { ...product, offers: updatedOffers, topOffer };
      }));
    };

    // Run cleanup on mount and every minute
    cleanupExpiredOffers();
    const interval = setInterval(cleanupExpiredOffers, 60000);
    return () => clearInterval(interval);
  }, []);

  // Cross-tab sync: detect logout in another tab
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // User logged out in another tab
      if (e.key === 'onlyjalen_user' && !e.newValue) {
        clearTokens();
        setIsAuthenticated(false);
        setIsAdminView(false);
        setCurrentUser(buyerUser);
        setActiveTab('home');
      }
      // User logged in/changed in another tab
      if (e.key === 'onlyjalen_user' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (isValidUser(parsed)) {
            const isAdmin = parsed.isAdmin === true;
            setCurrentUser({
              ...parsed,
              joinDate: parsed.joinDate ? new Date(parsed.joinDate) : new Date(),
              subscriptionExpiry: parsed.subscriptionExpiry ? new Date(parsed.subscriptionExpiry) : null,
              isAdmin,
            });
            setIsAuthenticated(true);
            setIsAdminView(isAdmin);
          }
        } catch {}
      }
      // Token cleared in another tab
      if (e.key === 'onlyjalen_access_token' && !e.newValue) {
        clearSavedUser();
        setIsAuthenticated(false);
        setIsAdminView(false);
        setCurrentUser(buyerUser);
        setActiveTab('home');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSelectedProduct(null);
  };

  const handleSignIn = async (email: string, password: string, isCreator: boolean) => {
    try {
      // Try real API authentication first
      const response = await authAPI.login(email, password);

      // Convert API user to frontend User type
      const apiUser = response.user as any;

      // Check if user is pending payment
      if (apiUser.status === 'pending') {
        const pendingInfo: PendingUserInfo = {
          email: apiUser.email,
          displayName: apiUser.display_name || email.split('@')[0],
          selectedTier: apiUser.selected_tier || 'monthly',
          // No paymentLink — user should check their email
          createdAt: apiUser.created_at || new Date().toISOString(),
        };

        savePendingUser(pendingInfo);
        setPendingUser(pendingInfo);
        setShowSignIn(false);
        return; // Don't authenticate - show pending page
      }
      const isAdminUser = apiUser.is_admin === 1 || apiUser.is_admin === true;
      const isSubscribedUser = apiUser.is_subscribed === 1 || apiUser.is_subscribed === true;
      const user: User = {
        id: apiUser.id,
        username: apiUser.username,
        displayName: apiUser.display_name,
        email: apiUser.email,
        avatar: apiUser.avatar || 'https://jalenward.com/images/LOGO.png',
        coverImage: '',
        bio: apiUser.bio || '',
        isAdmin: isAdminUser,
        isSubscribed: isSubscribedUser,
        isVerified: isAdminUser,
        subscriptionTier: apiUser.subscription_tier || null,
        subscriptionExpiry: apiUser.subscription_expiry ? new Date(apiUser.subscription_expiry) : null,
        subscribers: 0,
        posts: 0,
        likes: 0,
        earnings: 0,
        availableBalance: 0,
        pendingBalance: 0,
        joinDate: new Date(apiUser.created_at),
        online: true,
        shippingAddresses: [],
        wishlist: [],
        notificationPrefs: { newPosts: true, messages: true, orderUpdates: true, newProducts: true, promotions: false },
      };

      setCurrentUser(user);
      setIsAdminView(user.isAdmin);
      setIsAuthenticated(true);
      setShowSignIn(false);
      saveUser(user);  // Persist user for session restoration

      // Fetch posts from API
      fetchPostsFromAPI();

    } catch (apiError: any) {
      console.log('API auth error:', apiError);

      // Handle 402 Payment Required - pending users OR expired renewals
      if (apiError?.status === 402 || apiError?.message?.includes('Payment required')) {
        const errorData = apiError?.data || {};
        const isRenewal = errorData.status === 'expired' || errorData.renewalRequired === true;
        const pendingInfo: PendingUserInfo = {
          email: errorData.user?.email || email,
          displayName: errorData.user?.display_name || email.split('@')[0],
          selectedTier: errorData.plan || errorData.user?.selected_tier || 'monthly',
          createdAt: errorData.user?.created_at || new Date().toISOString(),
          renewal: isRenewal,
          periodEnd: errorData.periodEnd || null,
        };

        savePendingUser(pendingInfo);
        setPendingUser(pendingInfo);
        setShowSignIn(false);
        return; // Show pending page (renewal-mode if expired)
      }

      // Handle 403 No Subscription
      if (apiError?.status === 403 || apiError?.message?.includes('subscription required')) {
        // User exists but needs to purchase subscription
        alert('You need an active subscription to access OnlyJalen. Please select a subscription tier.');
        // Could redirect to subscription selection page
        return;
      }

      // Handle subscription_required status from registration
      if (apiError?.data?.status === 'subscription_required') {
        alert('Account created! Please select a subscription tier to continue.');
        return;
      }

      // For other errors (network, 401, etc.), fall back to mock credentials for testing
      console.log('Trying local credentials...');

      // Helper to generate mock JWT for demo/mock logins
      const generateMockToken = (user: User): string => {
        const payload = {
          sub: user.id,
          email: user.email,
          is_admin: user.isAdmin || false,
          exp: Math.floor(Date.now() / 1000) + (7 * 24 * 60 * 60), // 7 days
          iat: Math.floor(Date.now() / 1000)
        };
        const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
        const body = btoa(JSON.stringify(payload));
        return `${header}.${body}.mock-signature`;
      };

      // Fallback to local mock credentials
      const credential = userCredentials.find(
        cred => cred.email.toLowerCase() === email.toLowerCase() && cred.password === password
      );

      if (credential) {
        if (credential.isAdmin) {
          const user = { ...adminUser };
          setCurrentUser(user);
          setIsAdminView(true);
          saveUser(user);
          setTokens(generateMockToken(user), 'mock-refresh-token');
        } else {
          const user = { ...testSubscriber, email };
          setCurrentUser(user);
          setIsAdminView(false);
          saveUser(user);
          setTokens(generateMockToken(user), 'mock-refresh-token');
        }
        setIsAuthenticated(true);
        setShowSignIn(false);
      } else {
        // SECURITY: No demo mode - invalid credentials must fail
        throw new Error('Invalid credentials');
      }
    }
  };

  // Fetch posts from API
  const fetchPostsFromAPI = async () => {
    try {
      const [postsResponse, storiesResponse] = await Promise.allSettled([
        postsAPI.getAll(),
        postsAPI.getStories(),
      ]);

      const mapPost = (p: any): Post => ({
        id: p.id,
        content: p.content || '',
        mediaType: p.media_type || 'text',
        mediaUrl: p.media_url ? mediaAPI.fixMediaUrl(p.media_url) : undefined,
        posterUrl: p.poster_url ? mediaAPI.fixMediaUrl(p.poster_url) : undefined,
        isLocked: p.is_locked === 1 || p.is_locked === true,
        price: p.price,
        likes: p.likes || 0,
        comments: p.comments || 0,
        tips: p.tips || 0,
        likedBy: [],
        savedBy: [],
        timestamp: new Date(p.created_at),
        isStory: p.is_story === 1 || p.is_story === true,
        expiresAt: p.expires_at ? new Date(p.expires_at) : undefined,
      });

      const apiPosts: Post[] = postsResponse.status === 'fulfilled' && postsResponse.value.posts
        ? postsResponse.value.posts.map(mapPost)
        : [];

      const apiStories: Post[] = storiesResponse.status === 'fulfilled' && storiesResponse.value.posts
        ? storiesResponse.value.posts.map(mapPost)
        : [];

      const allApiPosts = [...apiPosts, ...apiStories];

      if (allApiPosts.length > 0) {
        const mockData = [...mockStories, ...mockPosts];
        const apiIds = new Set(allApiPosts.map(p => p.id));
        const uniqueMockPosts = mockData.filter(p => !apiIds.has(p.id));
        setPosts([...allApiPosts, ...uniqueMockPosts]);
      }
    } catch (error) {
      console.log('Failed to fetch posts from API, using local data:', error);
    }
  };

  // Fetch posts on mount if authenticated
  useEffect(() => {
    if (shouldFetchOnMount) {
      fetchPostsFromAPI();
    }
  }, [shouldFetchOnMount]);

  // Fetch notifications from API
  const fetchNotificationsFromAPI = async () => {
    try {
      const response = await notificationsAPI.getAll();
      // Always replace local state — even if the server returned 0 rows
      // (e.g., admin bulk-cleared or marked-read elsewhere). The previous
      // `length > 0` guard kept stale state forever in that case.
      const list = response.notifications || [];
      const apiNotifications: Notification[] = list.map((n: any) => ({
        id: n.id,
        type: n.type || 'message',
        title: n.title || 'Notification',
        message: n.message || '',
        timestamp: new Date(n.created_at),
        // D1 returns the boolean as INT 0/1 sometimes and string "0"/"1"
        // through certain JSON paths. !!Number handles both.
        read: !!Number(n.read),
        link: n.data ? JSON.parse(n.data)?.conversationId : undefined,
      }));
      setNotifications(apiNotifications);
    } catch (error) {
      console.log('Failed to fetch notifications from API:', error);
    }
  };

  // Fetch notifications on mount + poll every 30s so new notifications
  // appear without a manual reload. Same cadence as useOrders / useMessages.
  useEffect(() => {
    if (shouldFetchOnMount) {
      fetchNotificationsFromAPI();
      const id = setInterval(() => { fetchNotificationsFromAPI(); }, 30000);
      return () => clearInterval(id);
    }
  }, [shouldFetchOnMount]);

  // Fetch products from API. Merges server products with any local mock
  // products that don't already exist server-side, so the shop never
  // looks empty if the API is briefly unreachable.
  const fetchProductsFromAPI = async () => {
    try {
      const response = await productsAPI.getAll();
      if (!response?.products) return;
      const apiProducts: Product[] = response.products.map((p: any) => ({
        id: p.id,
        name: p.name,
        description: p.description || '',
        price: typeof p.price === 'number' ? p.price : Number(p.price) || 0,
        salePrice: p.sale_price != null ? Number(p.sale_price) : undefined,
        images: (() => {
          // Prefer the full images_json array (added 2026-05-17 so
          // ProductDetail's thumb gallery actually shows >1 image). Fall
          // back to single image_url for legacy rows.
          try {
            const arr = p.images_json ? JSON.parse(p.images_json) : null;
            if (Array.isArray(arr) && arr.length > 0) {
              return arr.map((u: string) => mediaAPI.fixMediaUrl(u));
            }
          } catch {}
          return p.image_url ? [mediaAPI.fixMediaUrl(p.image_url)] : ['https://jalenward.com/images/LOGO.png'];
        })(),
        category: p.category || 'General',
        sizes: [],
        stock: typeof p.stock === 'number' ? p.stock : 1,
        featured: p.featured === 1 || p.featured === true,
        createdAt: p.created_at ? new Date(p.created_at) : new Date(),
        sold: p.sold || 0,
        rating: p.rating || 0,
        reviews: p.reviews || 0,
        topOffer: p.top_offer || undefined,
      }));

      if (apiProducts.length === 0) return;

      setProducts(prev => {
        const apiIds = new Set(apiProducts.map(p => p.id));
        const carryLocal = prev.filter(p => !apiIds.has(p.id));
        return [...apiProducts, ...carryLocal];
      });
    } catch (e) {
      console.log('Failed to fetch products from API, using local data:', e);
    }
  };

  // Fetch products on first render (no auth required — shop is public-readable)
  useEffect(() => {
    fetchProductsFromAPI();
  }, []);

  // Fetch profile from API on mount to hydrate avatar/bio from DB
  const fetchProfileFromAPI = async () => {
    try {
      const response = await profileAPI.get();
      if (response?.user) {
        const apiUser = response.user;
        setCurrentUser(prev => {
          const updated = {
            ...prev,
            avatar: apiUser.avatar || prev.avatar,
            displayName: apiUser.display_name || prev.displayName,
            bio: apiUser.bio || prev.bio,
          };
          saveUser(updated);
          return updated;
        });
      }
    } catch {
      // Silent — don't break the page if profile fetch fails
    }
  };

  useEffect(() => {
    if (shouldFetchOnMount) {
      fetchProfileFromAPI();
    }
  }, [shouldFetchOnMount]);

  const handleSignUp = async (email: string, password: string, name: string, selectedTier?: string) => {
    try {
      // Call API registration - returns payment page URL
      const result = await authAPI.register(email, password, name.toLowerCase().replace(/\s+/g, '_'), name, selectedTier) as any;

      const pendingInfo: PendingUserInfo = {
        email,
        displayName: name,
        selectedTier: selectedTier || 'monthly',
        paymentLink: result?.approvalUrl || result?.paymentUrl || undefined,
        createdAt: new Date().toISOString(),
      };

      // Save pending user and show pending page
      savePendingUser(pendingInfo);
      setPendingUser(pendingInfo);
      setShowSignIn(false);

      // Note: API returns NO token for pending users
      // User stays in pending state until payment confirmed

    } catch (apiError: any) {
      console.error('Registration failed:', apiError);
      throw apiError; // Let the form handle the error
    }
  };

  // Resend payment email
  const handleResendPaymentEmail = async () => {
    if (!pendingUser) return;

    try {
      await authAPI.resendPaymentEmail(pendingUser.email);
    } catch (error) {
      console.log('API resend failed, using demo mode:', error);
      // Simulate success in demo mode
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  };

  // Check if pending user has completed payment
  const handleCheckPaymentStatus = async (): Promise<boolean> => {
    if (!pendingUser) return false;

    try {
      const result = await authAPI.checkPaymentStatus(pendingUser.email);

      if (result.status === 'active' && result.user) {
        // User has paid - convert to authenticated state
        const apiUser = result.user;
        const user: User = {
          id: apiUser.id,
          username: apiUser.username,
          displayName: apiUser.display_name,
          email: apiUser.email,
          avatar: apiUser.avatar || 'https://jalenward.com/images/LOGO.png',
          coverImage: '',
          bio: apiUser.bio || '',
          isAdmin: false,
          isSubscribed: true,
          isVerified: false,
          subscriptionTier: apiUser.subscription_tier,
          subscriptionExpiry: apiUser.subscription_expiry ? new Date(apiUser.subscription_expiry) : null,
          subscribers: 0,
          posts: 0,
          likes: 0,
          earnings: 0,
          availableBalance: 0,
          pendingBalance: 0,
          joinDate: new Date(apiUser.created_at),
          online: true,
          shippingAddresses: [],
          wishlist: [],
          notificationPrefs: { newPosts: true, messages: true, orderUpdates: true, newProducts: true, promotions: false },
        };

        // Clear pending state and authenticate
        clearPendingUser();
        setPendingUser(null);
        setCurrentUser(user);
        setIsAuthenticated(true);
        setShowSignIn(false);
        saveUser(user);
        return true;
      }

      return false;
    } catch (error) {
      console.log('API check failed, using demo mode:', error);
      // Simulate checking in demo mode
      await new Promise(resolve => setTimeout(resolve, 1500));
      return false;
    }
  };

  // Cancel pending registration and go back
  const handleCancelPending = () => {
    clearPendingUser();
    setPendingUser(null);
  };

  const handleSignOut = () => {
    // Show sign out page with animation
    setShowSignOut(true);
  };

  const completeSignOut = () => {
    clearTokens();
    clearSavedUser();
    setIsAuthenticated(false);
    setIsAdminView(false);
    setActiveTab('home');
    setCurrentUser(buyerUser);
    setShowSignOut(false);
    // Update URL without page refresh
    window.history.pushState({}, '', '/');
  };

  // Avatar upload function
  const updateAvatar = async (file: File): Promise<string> => {
    const result = await mediaAPI.uploadFile(file);
    if (!result.url) throw new Error('Upload failed: no URL returned');

    // Update local state + localStorage
    setCurrentUser(prev => {
      const updated = { ...prev, avatar: result.url };
      saveUser(updated);
      return updated;
    });

    // Persist to DB
    await profileAPI.updateAvatar(result.url).catch(console.error);

    return result.url;
  };

  const likePost = (postId: string) => {
    // Optimistic flip — UI updates immediately.
    const wasLiked = posts.find(p => p.id === postId)?.likedBy.includes(currentUser.id) ?? false;
    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        const isLiked = p.likedBy.includes(currentUser.id);
        return {
          ...p,
          likes: isLiked ? p.likes - 1 : p.likes + 1,
          likedBy: isLiked ? p.likedBy.filter(id => id !== currentUser.id) : [...p.likedBy, currentUser.id]
        };
      }
      return p;
    }));
    // Persist to backend. Pre-2026-05-17 likes vanished on reload because
    // the optimistic update was never followed by an API call. On failure,
    // we revert and surface a toast.
    postsAPI.like(postId).catch(err => {
      console.error('[likePost] API call failed, reverting:', err);
      setPosts(prev => prev.map(p => {
        if (p.id === postId) {
          // Revert to the pre-click state.
          return {
            ...p,
            likes: wasLiked ? p.likes + 1 : p.likes - 1,
            likedBy: wasLiked
              ? [...p.likedBy.filter(id => id !== currentUser.id), currentUser.id]
              : p.likedBy.filter(id => id !== currentUser.id),
          };
        }
        return p;
      }));
      import('./components/Toast/Toast').then(({ toast }) => toast.error('Like did not save')).catch(() => {});
    });
  };

  const savePost = (postId: string) => {
    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        const isSaved = p.savedBy.includes(currentUser.id);
        return {
          ...p,
          savedBy: isSaved ? p.savedBy.filter(id => id !== currentUser.id) : [...p.savedBy, currentUser.id]
        };
      }
      return p;
    }));
  };

  const tipCreator = (amount: number) => {
    const newNotif: Notification = {
      id: Date.now().toString(),
      type: 'tip',
      title: 'Tip Sent!',
      message: `You sent $${amount} tip`,
      timestamp: new Date(),
      read: false
    };
    setNotifications(prev => [newNotif, ...prev]);
  };

  const sendMessage = (content: string, mediaUrl?: string, price?: number) => {
    const newMsg: Message = {
      id: Date.now().toString(),
      fromUserId: currentUser.id,
      toUserId: 'admin',
      content,
      mediaUrl,
      timestamp: new Date(),
      read: false,
      price
    };
    setMessages(prev => [...prev, newMsg]);
  };

  const addToCart = (productId: string, size: string, quantity: number) => {
    setCart(prev => {
      const existing = prev.find(item => item.productId === productId && item.size === size);
      if (existing) {
        return prev.map(item => 
          item.productId === productId && item.size === size
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { productId, size, quantity, addedAt: new Date() }];
    });
  };

  const removeFromCart = (productId: string, size: string) => {
    setCart(prev => prev.filter(item => !(item.productId === productId && item.size === size)));
  };

  const updateCartQuantity = (productId: string, size: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId, size);
      return;
    }
    setCart(prev => prev.map(item =>
      item.productId === productId && item.size === size ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => setCart([]);

  const toggleWishlist = (productId: string) => {
    setCurrentUser(prev => ({
      ...prev,
      wishlist: prev.wishlist.includes(productId)
        ? prev.wishlist.filter(id => id !== productId)
        : [...prev.wishlist, productId]
    }));
  };

  const placeOrder = (shippingAddressId: string) => {
    const address = currentUser.shippingAddresses.find(a => a.id === shippingAddressId);
    if (!address) return;
    
    const items = cart.map(item => {
      const product = products.find(p => p.id === item.productId)!;
      return {
        productId: item.productId,
        productName: product.name,
        productImage: product.images[0],
        size: item.size,
        quantity: item.quantity,
        price: product.salePrice || product.price
      };
    });
    
    const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = 9.99;
    const tax = subtotal * 0.08;
    
    const newOrder: Order = {
      id: `ord${Date.now()}`,
      userId: currentUser.id,
      items,
      subtotal,
      shipping,
      tax,
      total: subtotal + shipping + tax,
      status: 'pending',
      shippingAddress: address,
      paypalTransactionId: `PP-${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    setOrders(prev => [newOrder, ...prev]);
    clearCart();
  };

  const updateOrderStatus = (orderId: string, status: Order['status']) => {
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status, updatedAt: new Date() } : o));
  };

  const subscribe = (tierId: string) => {
    setCurrentUser(prev => ({
      ...prev,
      isSubscribed: true,
      subscriptionTier: tierId,
      subscriptionExpiry: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    }));
  };

  const unlockContent = (postId: string) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, isLocked: false } : p));
  };

  const addProduct = async (product: Omit<Product, 'id' | 'createdAt' | 'sold' | 'rating' | 'reviews'>) => {
    // Persist server-side so the product shows up in the shop for every
    // user, not just the admin who created it. Fall back to local-only
    // insertion if the API errors so an offline admin demo still works.
    let serverId: string | null = null;
    try {
      const res = await productsAPI.create({
        name: product.name,
        description: product.description,
        price: product.price,
        originalPrice: product.salePrice,
        category: product.category,
        imageUrl: product.images?.[0],
        images: product.images,
        featured: product.featured,
        stock: product.stock,
      });
      if (res?.success && res.productId) serverId = res.productId;
    } catch (e) {
      console.warn('productsAPI.create failed, falling back to local-only product:', e);
    }

    const newProduct: Product = {
      ...product,
      id: serverId || `prod${Date.now()}`,
      createdAt: new Date(),
      sold: 0,
      rating: 0,
      reviews: 0
    };
    setProducts(prev => [...prev, newProduct]);

    // Re-pull canonical product list so other clients converge.
    fetchProductsFromAPI().catch(() => {});
  };

  const updateProduct = (productId: string, updates: Partial<Product>) => {
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, ...updates } : p));
  };

  const deleteProduct = (productId: string) => {
    const snapshot = products;
    setProducts(prev => prev.filter(p => p.id !== productId));
    productsAPI.remove(productId).catch(err => {
      console.error('productsAPI.remove failed', err);
      setProducts(snapshot);
    });
  };

  const createPost = (_post: Omit<Post, 'id' | 'likes' | 'comments' | 'tips' | 'likedBy' | 'savedBy' | 'timestamp'>) => {
    // Don't insert a local placeholder with a fake `p${Date.now()}` ID —
    // the parallel server POST creates a row with a different ID, and
    // both render together as a duplicate (bug reported 2026-05-17).
    // Instead, re-fetch from the API so the new post appears with its
    // canonical server-side ID. The arg is kept for backwards-compat
    // with the existing useApp() contract.
    fetchPostsFromAPI().catch(err => console.error('createPost re-fetch failed:', err));
  };

  const updatePost = (postId: string, updates: Partial<Post>) => {
    setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, ...updates } : p
    ));
  };

  const deletePost = (postId: string) => {
    // Optimistic local removal — the post disappears immediately.
    // Snapshot the current list so we can restore on API failure.
    const snapshot = posts;
    setPosts(prev => prev.filter(p => p.id !== postId));
    postsAPI.delete(postId).catch(err => {
      console.error('postsAPI.delete failed', err);
      setPosts(snapshot);
      try { (window as any).toast?.error?.('Could not delete post. Try again.'); } catch {}
    });
  };

  const updateProfile = (updates: Partial<User>) => {
    setCurrentUser(prev => {
      const updated = { ...prev, ...updates };
      saveUser(updated);
      return updated;
    });
  };

  const addShippingAddress = async (address: Omit<User['shippingAddresses'][0], 'id'>) => {
    // Persist server-side first. Pre-2026-05-17 this only mutated local
    // state, so every checkout sent a client-generated `addr${Date.now()}`
    // id the worker had never seen — orders saved with shipping_address_json
    // = null and admin saw "Address not yet captured" on every order.
    try {
      const res = await profileAPI.addAddress({
        name: (address as any).name,
        street: (address as any).street,
        city: (address as any).city,
        state: (address as any).state,
        zipCode: (address as any).zipCode,
        country: (address as any).country,
        phone: (address as any).phone,
        isDefault: (address as any).isDefault,
      } as any);
      const newAddress = { ...address, id: res.addressId } as any;
      setCurrentUser(prev => ({
        ...prev,
        shippingAddresses: [...prev.shippingAddresses, newAddress],
      }));
    } catch (e: any) {
      console.error('[addShippingAddress] API call failed:', e);
      // Fall back to local-only so the user isn't blocked, but warn
      // about checkout consequences.
      const newAddress = { ...address, id: `addr${Date.now()}${Math.random().toString(36).slice(2, 6)}` } as any;
      setCurrentUser(prev => ({
        ...prev,
        shippingAddresses: [...prev.shippingAddresses, newAddress],
      }));
      try {
        const { toast } = await import('./components/Toast/Toast');
        toast.error('Address saved locally — checkout may not work until you sign in again.');
      } catch {}
    }
  };

  const requestPayout = (amount: number) => {
    setCurrentUser(prev => ({
      ...prev,
      availableBalance: prev.availableBalance - amount,
      pendingBalance: prev.pendingBalance + amount
    }));
  };

  const markNotificationRead = async (notificationId: string) => {
    // Update local state immediately
    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
    // Persist to API
    try {
      await notificationsAPI.markAsRead(notificationId);
    } catch (error) {
      console.log('Failed to mark notification as read:', error);
    }
  };

  const addComment = async (postId: string, content: string) => {
    const user = isAdminView ? adminUser : currentUser;

    // Try API first
    try {
      const result = await commentsAPI.create(postId, content);
      if (result.success && result.comment) {
        // Convert API response to local format
        const newComment: Comment = {
          id: result.comment.id,
          postId,
          userId: result.comment.user_id,
          username: result.comment.display_name || result.comment.username,
          userAvatar: result.comment.avatar || user.avatar,
          content: result.comment.content,
          timestamp: new Date(result.comment.created_at),
          likes: 0,
          likedBy: []
        };

        setComments(prev => {
          const newMap = new Map(prev);
          const postComments = newMap.get(postId) || [];
          newMap.set(postId, [...postComments, newComment]);
          return newMap;
        });

        // Don't manually +1 here — next fetchPostsFromAPI returns the
        // server's COUNT(*) which already includes the new row. Local
        // increment + later refresh caused steady drift (the comment
        // count grew faster than the actual comments did).
        return;
      }
    } catch (e) {
      console.log('API unavailable for comments, using localStorage fallback:', e);
    }

    // Fallback to localStorage
    const newComment: Comment = {
      id: `c${Date.now()}`,
      postId,
      userId: user.id,
      username: user.displayName,
      userAvatar: user.avatar,
      content,
      timestamp: new Date(),
      likes: 0,
      likedBy: []
    };

    setComments(prev => {
      const newMap = new Map(prev);
      const postComments = newMap.get(postId) || [];
      newMap.set(postId, [...postComments, newComment]);
      return newMap;
    });

    // Update post comment count
    setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, comments: p.comments + 1 } : p
    ));
  };

  const likeComment = (postId: string, commentId: string) => {
    const user = isAdminView ? adminUser : currentUser;
    setComments(prev => {
      const newMap = new Map(prev);
      const postComments = newMap.get(postId) || [];
      const updatedComments = postComments.map(c => {
        if (c.id === commentId) {
          const isLiked = c.likedBy.includes(user.id);
          return {
            ...c,
            likes: isLiked ? c.likes - 1 : c.likes + 1,
            likedBy: isLiked
              ? c.likedBy.filter(id => id !== user.id)
              : [...c.likedBy, user.id]
          };
        }
        return c;
      });
      newMap.set(postId, updatedComments);
      return newMap;
    });
  };

  const getComments = (postId: string): Comment[] => {
    return comments.get(postId) || [];
  };

  const makeOffer = async (productId: string, amount: number, message?: string) => {
    const user = isAdminView ? adminUser : currentUser;
    const product = products.find(p => p.id === productId);

    // Try API first
    try {
      const result = await offersAPI.create(productId, amount, message);
      if (result.success) {
        const newOffer: ProductOffer = {
          id: result.offerId,
          userId: user.id,
          username: user.displayName,
          amount,
          message,
          timestamp: new Date(),
          expiresAt: new Date(result.expiresAt),
          status: 'pending',
        };

        setProducts(prev => prev.map(p => {
          if (p.id === productId) {
            const offers = [...(p.offers || []), newOffer];
            const topOffer = Math.max(...offers.map(o => o.amount));
            return { ...p, offers, topOffer };
          }
          return p;
        }));

        // Add notification
        const newNotif: Notification = {
          id: Date.now().toString(),
          type: 'product',
          title: 'Offer Submitted',
          message: `Your $${amount} offer has been sent`,
          timestamp: new Date(),
          read: false
        };
        setNotifications(prev => [newNotif, ...prev]);
        return;
      }
    } catch (e) {
      console.log('API unavailable for offers, using localStorage fallback:', e);
    }

    // Fallback to localStorage
    const expirationDays = 7;
    const newOffer: ProductOffer = {
      id: `offer_${Date.now()}`,
      userId: user.id,
      username: user.displayName,
      amount,
      message,
      timestamp: new Date(),
      expiresAt: new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000),
      status: 'pending',
    };

    setProducts(prev => prev.map(p => {
      if (p.id === productId) {
        const offers = [...(p.offers || []), newOffer];
        const topOffer = Math.max(...offers.map(o => o.amount));
        return { ...p, offers, topOffer };
      }
      return p;
    }));

    // Also send as message to creator (fallback only)
    if (product) {
      const offerMessage = `OFFER: $${amount} for "${product.name}"${message ? `\n\nMessage: ${message}` : ''}`;
      sendMessage(offerMessage);
    }

    // Add notification
    const newNotif: Notification = {
      id: Date.now().toString(),
      type: 'product',
      title: 'Offer Submitted',
      message: `Your $${amount} offer has been sent`,
      timestamp: new Date(),
      read: false
    };
    setNotifications(prev => [newNotif, ...prev]);
  };

  const getProductOffers = (productId: string): ProductOffer[] => {
    const product = products.find(p => p.id === productId);
    return product?.offers || [];
  };

  // Get all offers for the current user (buyer view)
  const getUserOffers = (): { product: Product; offer: ProductOffer }[] => {
    const user = isAdminView ? adminUser : currentUser;
    const userOffers: { product: Product; offer: ProductOffer }[] = [];
    products.forEach(product => {
      product.offers?.forEach(offer => {
        if (offer.userId === user.id) {
          userOffers.push({ product, offer });
        }
      });
    });
    return userOffers.sort((a, b) =>
      new Date(b.offer.timestamp).getTime() - new Date(a.offer.timestamp).getTime()
    );
  };

  // Get all pending offers across all products (creator view)
  const getAllPendingOffers = (): { product: Product; offer: ProductOffer }[] => {
    const pendingOffers: { product: Product; offer: ProductOffer }[] = [];
    products.forEach(product => {
      product.offers?.forEach(offer => {
        if (offer.status === 'pending') {
          pendingOffers.push({ product, offer });
        }
      });
    });
    return pendingOffers.sort((a, b) => b.offer.amount - a.offer.amount);
  };

  // Accept an offer (creator action)
  const acceptOffer = async (productId: string, offerId: string) => {
    // Gate the local state mutation on API success. Pre-2026-05-17 the
    // local flip ran *unconditionally* after the catch — buyer saw
    // "Offer Accepted!" + notification even though the server still had
    // the offer pending.
    let apiSucceeded = false;
    try {
      const result = await offersAPI.accept(offerId);
      apiSucceeded = result?.success === true;
    } catch (e) {
      console.error('[acceptOffer] API call failed:', e);
    }
    if (!apiSucceeded) {
      const { toast } = await import('./components/Toast/Toast');
      toast.error('Could not accept offer — please try again.');
      return;
    }

    // Local state mirror (API succeeded)
    setProducts(prev => prev.map(product => {
      if (product.id !== productId || !product.offers) return product;

      const updatedOffers = product.offers.map(offer => {
        if (offer.id === offerId) {
          return { ...offer, status: 'accepted' as const };
        }
        // Decline all other pending offers on this product
        if (offer.status === 'pending') {
          return { ...offer, status: 'rejected' as const };
        }
        return offer;
      });

      return { ...product, offers: updatedOffers, topOffer: undefined };
    }));

    // Find the offer and notify the user
    const product = products.find(p => p.id === productId);
    const offer = product?.offers?.find(o => o.id === offerId);
    if (offer) {
      const newNotif: Notification = {
        id: Date.now().toString(),
        type: 'product',
        title: 'Offer Accepted!',
        message: `Your $${offer.amount} offer for "${product?.name}" was accepted`,
        timestamp: new Date(),
        read: false
      };
      setNotifications(prev => [newNotif, ...prev]);
    }
  };

  // Decline an offer (creator action)
  const declineOffer = async (productId: string, offerId: string) => {
    // Same gate as acceptOffer — don't flip local state if the API
    // rejected (race, expired offer, etc.).
    let apiSucceeded = false;
    try {
      const result = await offersAPI.decline(offerId);
      apiSucceeded = result?.success === true;
    } catch (e) {
      console.error('[declineOffer] API call failed:', e);
    }
    if (!apiSucceeded) {
      const { toast } = await import('./components/Toast/Toast');
      toast.error('Could not decline offer — please try again.');
      return;
    }

    // Local state mirror (API succeeded)
    setProducts(prev => prev.map(product => {
      if (product.id !== productId || !product.offers) return product;

      const updatedOffers = product.offers.map(offer =>
        offer.id === offerId ? { ...offer, status: 'rejected' as const } : offer
      );

      // Recalculate top offer
      const activeOffers = updatedOffers.filter(o => o.status === 'pending');
      const topOffer = activeOffers.length > 0
        ? Math.max(...activeOffers.map(o => o.amount))
        : undefined;

      return { ...product, offers: updatedOffers, topOffer };
    }));
  };

  // Counter an offer (creator action)
  const counterOffer = async (productId: string, offerId: string, counterAmount: number) => {
    let apiSucceeded = false;
    try {
      const result = await offersAPI.counter(offerId, counterAmount);
      apiSucceeded = result?.success === true;
    } catch (e) {
      console.error('[counterOffer] API call failed:', e);
    }
    if (!apiSucceeded) {
      const { toast } = await import('./components/Toast/Toast');
      toast.error('Could not send counter offer — please try again.');
      return;
    }

    // Local state mirror (API succeeded)
    setProducts(prev => prev.map(product => {
      if (product.id !== productId || !product.offers) return product;

      const updatedOffers = product.offers.map(offer =>
        offer.id === offerId
          ? { ...offer, status: 'countered' as const, counterAmount }
          : offer
      );

      return { ...product, offers: updatedOffers };
    }));

    // Notify the buyer
    const product = products.find(p => p.id === productId);
    const offer = product?.offers?.find(o => o.id === offerId);
    if (offer) {
      const newNotif: Notification = {
        id: Date.now().toString(),
        type: 'product',
        title: 'Counter Offer Received',
        message: `${product?.name}: Counter offer of $${counterAmount} (you offered $${offer.amount})`,
        timestamp: new Date(),
        read: false
      };
      setNotifications(prev => [newNotif, ...prev]);
    }
  };

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      if (!product) return sum;
      return sum + (product.salePrice || product.price) * item.quantity;
    }, 0);
  }, [cart, products]);

  const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);

  const renderPage = () => {
    // Admin view - show unified dashboard on home
    if (isAdminView) {
      return <AdminDashboard />;
    }

    // Regular user view
    if (selectedProduct && activeTab === 'shop') {
      return <ProductDetail />;
    }

    switch (activeTab) {
      case 'home': return <HomePage />;
      case 'shop': return <ShopPage />;
      case 'cart': return <CartPage />;
      case 'messages': return <MessagesPage />;
      case 'profile': return <ProfilePage onSignOut={handleSignOut} />;
      default: return <HomePage />;
    }
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;
  const unreadMessages = messages.filter(m => !m.read && m.toUserId === currentUser.id).length;

  // Show pending page FIRST — takes priority over all other unauthenticated routes
  if (pendingUser) {
    return (
      <PendingPage
        user={pendingUser}
        subscriptionTiers={subscriptionTiers}
        onResendEmail={handleResendPaymentEmail}
        onBack={handleCancelPending}
        onCheckStatus={handleCheckPaymentStatus}
      />
    );
  }

  // Show landing page (or guest shop) if not authenticated
  if (!isAuthenticated && !showSignIn) {
    if (guestPath === '/shop') {
      return (
        <GuestShopPage
          onSignIn={() => { setSignInMode('signin'); setShowSignIn(true); }}
          onBackToLanding={() => navigateGuest('/')}
        />
      );
    }
    return (
      <LandingPage
        onGetStarted={() => { setSignInMode('signup'); setShowSignIn(true); }}
        onSignIn={() => { setSignInMode('signin'); setShowSignIn(true); }}
        onShopClick={() => navigateGuest('/shop')}
      />
    );
  }

  // Show sign in page
  if (!isAuthenticated && showSignIn) {
    return <SignInPage onSignIn={handleSignIn} onSignUp={handleSignUp} onBack={() => { setShowSignIn(false); setSignInMode('signin'); }} subscriptionTiers={subscriptionTiers} defaultMode={signInMode} />;
  }

  // Show sign out page with animation
  if (showSignOut) {
    return <SignOutPage onComplete={completeSignOut} />;
  }

  // Admin gets simplified nav - no shop/cart/profile (all in dashboard)
  const navItems = isAdminView ? [
    { id: 'home', label: 'Dashboard', icon: 'home' }
  ] : [
    { id: 'home', label: 'Home', icon: 'home' },
    { id: 'shop', label: 'Shop', icon: 'shop' },
    { id: 'cart', label: 'Cart', icon: 'cart', badge: cartCount },
    { id: 'messages', label: 'Messages', icon: 'messages', badge: unreadMessages },
    { id: 'profile', label: 'Profile', icon: 'profile' }
  ];

  return (
    <AppContext.Provider value={{
      currentUser: currentUser,
      adminUser,
      posts,
      products,
      cart,
      orders,
      messages,
      notifications,
      subscriptionTiers,
      analytics,
      comments,
      activeTab,
      setActiveTab: handleTabChange,
      selectedProduct,
      setSelectedProduct,
      viewMode,
      setViewMode,
      likePost,
      savePost,
      tipCreator,
      sendMessage,
      addToCart,
      removeFromCart,
      updateCartQuantity,
      clearCart,
      toggleWishlist,
      placeOrder,
      updateOrderStatus,
      subscribe,
      unlockContent,
      addProduct,
      updateProduct,
      deleteProduct,
      createPost,
      updatePost,
      deletePost,
      updateProfile,
      updateAvatar,
      addShippingAddress,
      requestPayout,
      markNotificationRead,
      addComment,
      likeComment,
      getComments,
      makeOffer,
      getProductOffers,
      getUserOffers,
      getAllPendingOffers,
      acceptOffer,
      declineOffer,
      counterOffer,
      cartTotal,
      cartCount,
      isAdminView,
      setIsAdminView,
      signOut: handleSignOut
    }}>
      <div className={`app-container ${isAdminView ? 'admin-mode' : ''}`}>
        {/* Desktop Sidebar - hidden for admin (dashboard has its own) */}
        {!isAdminView && (
        <aside className="desktop-sidebar">
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <img src="https://jalenward.com/images/logosvg.svg" alt="OnlyJalen" style={{ height: '40px', objectFit: 'contain' }} />
            </div>
          </div>
          <nav className="sidebar-nav">
            {navItems.map(item => (
              <button
                key={item.id}
                className={`sidebar-nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => handleTabChange(item.id)}
              >
                {item.icon === 'home' && (
                  <svg viewBox="0 0 24 24" fill={activeTab === item.id ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={activeTab === item.id ? 0 : 2}>
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                  </svg>
                )}
                {item.icon === 'shop' && (
                  <svg viewBox="0 0 24 24" fill={activeTab === item.id ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={activeTab === item.id ? 0 : 2}>
                    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                    <line x1="3" y1="6" x2="21" y2="6" stroke={activeTab === item.id ? 'white' : 'currentColor'} strokeWidth="2"/>
                    <path d="M16 10a4 4 0 01-8 0" stroke={activeTab === item.id ? 'white' : 'currentColor'} strokeWidth="2" fill="none"/>
                  </svg>
                )}
                {item.icon === 'cart' && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={activeTab === item.id ? 2.5 : 2}>
                    <circle cx="9" cy="21" r="1"/>
                    <circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
                  </svg>
                )}
                {item.icon === 'messages' && (
                  <svg viewBox="0 0 24 24" fill={activeTab === item.id ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={activeTab === item.id ? 0 : 2}>
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                )}
                {item.icon === 'profile' && (
                  <svg viewBox="0 0 24 24" fill={activeTab === item.id ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={activeTab === item.id ? 0 : 2}>
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                )}
                {item.label}
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="nav-badge">{item.badge > 9 ? '9+' : item.badge}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="sidebar-footer">
            <div className="sidebar-profile" onClick={() => handleTabChange('profile')}>
              <img src={currentUser.avatar || adminUser.avatar} alt="Profile" />
              <div className="sidebar-profile-info">
                <span className="sidebar-profile-name">{currentUser.displayName || adminUser.displayName}</span>
                <span className="sidebar-profile-role">{isAdminView ? 'Creator' : 'Subscriber'}</span>
              </div>
            </div>
          </div>
        </aside>
        )}

        {/* Main App */}
        <div className="app-main-wrapper">
          {isAdminView ? (
            // Admin view - full screen dashboard with its own nav
            <div className="admin-full-screen">
              {renderPage()}
            </div>
          ) : (
            // Regular user view with header/nav
            <div className={`app ${viewMode}-mode`}>
              {currentUser.isAdmin && (
                <AdminPreviewBar onExit={() => setIsAdminView(true)} />
              )}
              <Header
                isAdminView={isAdminView}
                setIsAdminView={setIsAdminView}
                unreadNotifications={unreadNotifications}
                onSignOut={handleSignOut}
              />
              <main className="app-main">
                {renderPage()}
              </main>
              <div className="mobile-bottom-nav">
                <BottomNav
                  cartCount={cartCount}
                  unreadMessages={unreadMessages}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - only for non-admin */}
        {!isAdminView && (
        <aside className="desktop-right-sidebar">
          {/* Creator card — real info, no invented numbers */}
          <div className="right-sidebar-card creator-card">
            <div className="creator-card-cover">
              {adminUser.coverImage && (
                <img
                  src={adminUser.coverImage}
                  alt=""
                  onError={e => {
                    const img = e.target as HTMLImageElement;
                    img.style.display = 'none';
                  }}
                />
              )}
            </div>
            <img src={adminUser.avatar} alt={adminUser.displayName} className="creator-card-avatar" />
            <div className="creator-card-body">
              <h4>
                {adminUser.displayName}
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
              </h4>
              <p className="creator-card-handle">@{adminUser.username}</p>
              {adminUser.online && (
                <span className="creator-card-online"><span className="dot" /> Online now</span>
              )}
              {adminUser.bio && (
                <p className="creator-card-bio">{adminUser.bio.split('\n')[0]}</p>
              )}
            </div>
          </div>

          {/* Subscription status — real plan + expiry */}
          {currentUser.isSubscribed && currentUser.subscriptionTier && (
            <div className="right-sidebar-card sub-card">
              <h3>Your subscription</h3>
              <div className="sub-card-row">
                <span>Plan</span>
                <strong>{currentUser.subscriptionTier === 'monthly' ? '1 Month'
                  : currentUser.subscriptionTier === 'quarterly' ? '3 Months'
                  : currentUser.subscriptionTier === 'yearly' ? '1 Year'
                  : currentUser.subscriptionTier === 'lifetime' ? 'Lifetime'
                  : currentUser.subscriptionTier}</strong>
              </div>
              {currentUser.subscriptionExpiry && (
                <div className="sub-card-row">
                  <span>Renews</span>
                  <strong>{new Date(currentUser.subscriptionExpiry).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</strong>
                </div>
              )}
              <div className="sub-card-row">
                <span>Status</span>
                <strong className="sub-card-status">Active</strong>
              </div>
            </div>
          )}
        </aside>
        )}
      </div>
      <ToastContainer />
      {spotlightProduct && (
        <FeaturedSpotlightModal
          product={spotlightProduct}
          onClose={() => dismissSpotlight('later')}
          onView={() => dismissSpotlight('view')}
        />
      )}
      {/* First-login subscriber tutorial — renders once per browser for
          non-admin authenticated users. Persists "seen" to localStorage. */}
      {isAuthenticated && !isAdminView && showTutorial && (
        <FirstLoginTutorial
          firstName={(currentUser?.displayName || currentUser?.email || '').split(/[\s.@]/)[0] || undefined}
          onDismiss={() => setShowTutorial(false)}
        />
      )}
    </AppContext.Provider>
  );
}

export default App;
